import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Loading from '../../Common/Loading'
import '../Style/UserDiary.css'

interface OptionItem {
  value: string
  label: string
}

interface DiaryEntry {
  dateTime: string
  activity: string
  score: string
  timeTaken: string
  attempts: string
  level: string
  unit: string
  chapter: string
  selectedFrom: string
}

interface UserDiaryInitResponse {
  familyCodeLabel: string
  logoUrl: string
  logins: OptionItem[]
  subjects: OptionItem[]
  selectedLogin: string
  selectedSubject: string
}

const UserDiary: React.FC = () => {
  const [searchParams] = useSearchParams()
  const apiBaseUrl = process.env.REACT_APP_DOCUMENT_LINK || '/'

  const schoolCode = searchParams.get('SchoolCode') || sessionStorage.getItem('S') || ''
  const familyCode = searchParams.get('FamilyCode') || sessionStorage.getItem('F') || ''
  const loginParam = searchParams.get('Login') || sessionStorage.getItem('L') || ''

  const today = useMemo(() => new Date().toISOString().split('T')[0], [])

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [familyCodeLabel, setFamilyCodeLabel] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [logins, setLogins] = useState<OptionItem[]>([])
  const [subjects, setSubjects] = useState<OptionItem[]>([])
  const [selectedLogin, setSelectedLogin] = useState('')
  const [selectedSubject, setSelectedSubject] = useState('')
  const [period, setPeriod] = useState<'Daily' | 'Weekly' | 'Monthly' | 'ViewFrom'>('Daily')
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [entries, setEntries] = useState<DiaryEntry[]>([])

  useEffect(() => {
    const loadPage = async () => {
      if (!schoolCode || !familyCode) {
        setError('SchoolCode and FamilyCode are required to open User Diary.')
        return
      }

      setLoading(true)
      setError('')

      try {
        const params = new URLSearchParams({
          SchoolCode: schoolCode,
          FamilyCode: familyCode,
          Login: loginParam,
        })

        const response = await fetch(`${apiBaseUrl}api/user-diary-react-init/?${params.toString()}`)
        if (!response.ok) {
          throw new Error('Failed to load User Diary.')
        }

        const data: UserDiaryInitResponse = await response.json()
        setFamilyCodeLabel(data.familyCodeLabel || familyCode)
        setLogoUrl(data.logoUrl || '')
        setLogins(data.logins || [])
        setSubjects(data.subjects || [])
        setSelectedLogin(data.selectedLogin || '')
        setSelectedSubject(data.selectedSubject || '')
      } catch (err) {
        console.error(err)
        setError('Unable to load User Diary.')
      } finally {
        setLoading(false)
      }
    }

    loadPage()
  }, [apiBaseUrl, familyCode, loginParam, schoolCode])

  const handleSubmit = async () => {
    if (!selectedLogin) {
      setError('Please select a login.')
      return
    }

    if (!selectedSubject) {
      setError('Please select a subject.')
      return
    }

    if (period === 'ViewFrom' && (!fromDate || !toDate)) {
      setError('Please select both From and To dates.')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const params = new URLSearchParams({
        SchoolCode: schoolCode,
        FamilyCode: familyCode,
        Login: selectedLogin,
        Subject: selectedSubject,
        Period: period,
      })

      if (period === 'ViewFrom') {
        params.append('FromDate', fromDate)
        params.append('ToDate', toDate)
      }

      const response = await fetch(`${apiBaseUrl}api/user-diary-react-report/?${params.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to load diary report.')
      }

      const data = await response.json()
      setEntries(data.entries || [])
    } catch (err) {
      console.error(err)
      setError('Unable to load diary entries.')
      setEntries([])
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      {(loading || submitting) && <Loading />}

      <div id="main_top_container" >
        <div id="header">
          {logoUrl ? <img id="imgLogo" className="scrolled_logo" src={logoUrl} alt="Logo" /> : null}
        </div>
      </div>

      <div id="user_banner"></div>
      <div id="divSubjectBanner" className="subject_banner"></div>

      <div className="section-container" id="studentreportscollapse">
        <div className="StudentDetails">
          <span>FamilyCode:</span>
          <span>{familyCodeLabel}</span>

          <span>Login:</span>
          <select
            id="ddlLogins"
            value={selectedLogin}
            onChange={e => setSelectedLogin(e.target.value)}
          >
            {logins.map(item => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <span>Subject:</span>
          <select
            id="ddlSubject"
            value={selectedSubject}
            onChange={e => setSelectedSubject(e.target.value)}
          >
            {subjects.map(item => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="radio-mobile-container">
          <div className="radio-container">
            <label>
              <input
                type="radio"
                name="period"
                checked={period === 'Daily'}
                onChange={() => setPeriod('Daily')}
              />
              Daily
            </label>
          </div>

          <div className="radio-container">
            <label>
              <input
                type="radio"
                name="period"
                checked={period === 'Weekly'}
                onChange={() => setPeriod('Weekly')}
              />
              Weekly
            </label>
          </div>

          <div className="radio-container">
            <label>
              <input
                type="radio"
                name="period"
                checked={period === 'Monthly'}
                onChange={() => setPeriod('Monthly')}
              />
              Monthly
            </label>
          </div>

          <div className="radio-container">
            <label>
              <input
                type="radio"
                name="period"
                checked={period === 'ViewFrom'}
                onChange={() => setPeriod('ViewFrom')}
              />
              View From:
            </label>
            <input
              type="date"
              value={fromDate}
              disabled={period !== 'ViewFrom'}
              onChange={e => setFromDate(e.target.value)}
            />
            <span style={{ margin: '0 6px' }}>To:</span>
            <input
              type="date"
              value={toDate}
              disabled={period !== 'ViewFrom'}
              onChange={e => setToDate(e.target.value)}
            />
          </div>

          <div className="radio-container">
            <button id="btnSubmit" type="button" onClick={handleSubmit}>
              Submit
            </button>
          </div>
        </div>

        {error ? (
          <div style={{ color: '#c0392b', margin: '16px 0', textAlign: 'center' }}>{error}</div>
        ) : null}

        <br />
        <br />

        <div className="widget-container">
          <div className="table-mobile-scroll">&#8592; Scroll table horizontally to view more &#8594;</div>
          <div className="scrolling-table-container">
            <table>
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th className="activity-max-length">Activity</th>
                  <th>Score</th>
                  <th>
                    <div className="th-double-line">
                      Time Taken
                      <br />
                      (mins)
                    </div>
                  </th>
                  <th>
                    <div className="th-double-line">
                      No. of
                      <br />
                      attempts
                    </div>
                  </th>
                  <th>Level</th>
                  <th>Unit</th>
                  <th>Chapter</th>
                  <th>Selected From</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '20px' }}>
                      No diary entries found.
                    </td>
                  </tr>
                ) : (
                  entries.map((entry, index) => (
                    <tr key={`${entry.dateTime}-${entry.activity}-${index}`}>
                      <td>{entry.dateTime}</td>
                      <td>{entry.activity}</td>
                      <td>{entry.score}</td>
                      <td>{entry.timeTaken}</td>
                      <td>{entry.attempts}</td>
                      <td className="td-max-length">{entry.level}</td>
                      <td className="td-max-length">{entry.unit}</td>
                      <td className="td-max-length">{entry.chapter}</td>
                      <td>{entry.selectedFrom}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserDiary
